import React, { useState, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormControl, FormHelperText } from '@mui/material';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import { Typography } from '@totalsoft/rocket-ui';
import useEventCallback from 'utils/hooks/useEventCallback';
import debounce from 'lodash.debounce';
import {
  FilterSliderFieldLabelDropdown,
  FilterSliderFieldLabelText,
  FilterSliderFieldLabelWrapper,
  FilterSliderFieldValue,
  FilterSliderFieldWrapper,
  FilterSliderLabel,
  FilterSliderLabelWrapper,
  StyledFilterSlider
} from './ProductSelectionStyles';

const FilterSlider = ({
  id,
  label,
  fullWidth,
  value: externalValue,
  fieldValue,
  onFieldValueChange,
  onValueChangeForFieldValue,
  fieldLabel,
  fieldLabelOptions,
  onFieldLabelChange,
  fieldDecimalScale,
  min,
  max,
  minMaxLabel,
  step,
  onChange,
  error,
  helperText,
  required,
  decimalScale,
  disabled,
  showSliderLimits,
  valueLabelFormat,
  ...other
}) => {
  const [internalValue, setInternalValue] = useState(externalValue);
  const prevExternalValue = useRef(externalValue);

  const setExternalValue = useEventCallback(x => x !== externalValue && onChange?.(null, x));
  const setExternalValueDebounced = useMemo(() => debounce(setExternalValue, 500), [setExternalValue]);

  if (prevExternalValue.current !== externalValue) {
    setInternalValue(externalValue);
    if (onValueChangeForFieldValue != null) onValueChangeForFieldValue(externalValue);
    prevExternalValue.current = externalValue;
  }

  const handleBlur = useCallback(
    function (_event) {
      const value = internalValue ? Math.min(max, Math.max(internalValue, min)) : min;
      setInternalValue(value);
      if (onValueChangeForFieldValue != null) {
        onValueChangeForFieldValue(value);
      }
      setExternalValue(value);
    },
    [internalValue, max, min, onValueChangeForFieldValue, setExternalValue]
  );

  const handleValueChanged = useCallback(
    (_, value) => {
      setInternalValue(value);
      if (onValueChangeForFieldValue != null) onValueChangeForFieldValue(value);
      setExternalValueDebounced(value);
    },
    [onValueChangeForFieldValue, setExternalValueDebounced]
  );

  const internalHandleFieldLabelChange = useCallback(
    value => {
      onFieldLabelChange(value);
    },
    [onFieldLabelChange]
  );

  const onChangeFieldValueInternal = useCallback(
    value => {
      if (onFieldValueChange != null) {
        const rez = onFieldValueChange(value);
        setInternalValue(rez);
      } else setInternalValue(value);
    },
    [onFieldValueChange]
  );

  const internalValueLabelFormat = useCallback(
    _ => {
      if (valueLabelFormat != null) return valueLabelFormat(internalValue);
      return internalValue;
    },
    [valueLabelFormat, internalValue]
  );

  return (
    <FormControl fullWidth={fullWidth} error={error} required={required}>
      <Grid container pb={1} alignContent='center'>
        <FilterSliderLabelWrapper item xs>
          <Typography variant='caption' fontWeight='bold'>
            {label}
          </Typography>
        </FilterSliderLabelWrapper>
        <FilterSliderFieldWrapper item container>
          <Grid item>
            <FilterSliderFieldValue
              debounceBy={1}
              id={id}
              required={required}
              isNumeric
              value={fieldValue != null ? fieldValue : internalValue}
              onBlur={handleBlur}
              onChange={onChangeFieldValueInternal}
              inputProps={{ decimalScale: fieldDecimalScale != null ? fieldDecimalScale : decimalScale, color: 'red' }}
              InputProps={{ disableUnderline: true, color: 'red' }}
              debounced={false}
              disabled={disabled}
            />
          </Grid>
          {fieldLabel && (
            <FilterSliderFieldLabelWrapper item container alignItems='center' height={1}>
              {fieldLabelOptions && fieldLabelOptions.length > 1 ? (
                <FilterSliderFieldLabelDropdown
                  fullWidth
                  select
                  value={fieldLabel}
                  onChange={internalHandleFieldLabelChange}
                  InputProps={{ disableUnderline: true }}
                  disabled={disabled}
                >
                  {fieldLabelOptions &&
                    fieldLabelOptions.length > 1 &&
                    fieldLabelOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </FilterSliderFieldLabelDropdown>
              ) : (
                <FilterSliderFieldLabelText disabled={disabled}>{fieldLabel}</FilterSliderFieldLabelText>
              )}
            </FilterSliderFieldLabelWrapper>
          )}
        </FilterSliderFieldWrapper>
      </Grid>
      <StyledFilterSlider
        value={internalValue}
        min={min}
        max={max}
        step={step}
        aria-labelledby='continuous-slider'
        onChange={handleValueChanged}
        disabled={disabled}
        valueLabelDisplay='on'
        valueLabelFormat={internalValueLabelFormat}
        error={error}
        {...other}
      />
      {showSliderLimits && (
        <Grid container justifyContent='space-between'>
          <FilterSliderLabel variant='overline'>
            {min ? min.toFixed(decimalScale) : min} {minMaxLabel}
          </FilterSliderLabel>
          <FilterSliderLabel variant='overline'>
            {max ? max.toFixed(decimalScale) : max} {minMaxLabel}
          </FilterSliderLabel>
        </Grid>
      )}
      <FormHelperText error={error}>{helperText}</FormHelperText>
    </FormControl>
  );
};

FilterSlider.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  fullWidth: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  minMaxLabel: PropTypes.string,
  step: PropTypes.number,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  decimalScale: PropTypes.number,
  disabled: PropTypes.bool,
  showSliderLimits: PropTypes.bool,
  fieldValue: PropTypes.any,
  onFieldValueChange: PropTypes.func,
  fieldLabel: PropTypes.string,
  fieldLabelOptions: PropTypes.array,
  onFieldLabelChange: PropTypes.func,
  valueLabelFormat: PropTypes.func,
  onValueChangeForFieldValue: PropTypes.func,
  fieldDecimalScale: PropTypes.number
};

export default FilterSlider;
